import {
  MA_PERIODS, MA_PRESETS, PANE_OPTIONS, PANE_MAX_COUNT,
  type MaPeriod, type OverlayId, type PaneId, type ValuationBasis, type ValuationKind,
} from '../../lib/app';
import { Popover, PopoverCheck, PopoverGroup } from './Popover';
import { useState } from 'react';

const OVERLAYS: { id: OverlayId; label: string; hint: string }[] = [
  { id: 'ma', label: 'MA 均线', hint: '移动平均线，勾选后在右侧选周期' },
  { id: 'boll', label: 'BOLL 布林带', hint: '20 日均线 ±2 倍标准差' },
  { id: 'bbi', label: 'BBI 多空线', hint: '3/6/12 日均线的平均' },
  { id: 'valuation', label: '估值', hint: 'PE / 股息率曲线（中证数据）' },
];

/** 带二级设置的叠加指标 → 右栏的切换项 */
type SubTab = 'ma' | 'valuation';

const SUB_LABEL: Record<SubTab, string> = { ma: 'MA 周期', valuation: '估值' };

/** 无二级设置的右栏宽度：只有一个窄列时不必占满双栏 */
const POPOVER_WIDE = 396;
const POPOVER_NARROW = 252;

/**
 * 主图叠加配置 —— 单个「指标 ▾」下拉，MA 周期设置并入其右栏。
 *
 * 改前有两个下拉：「指标 ▾」选叠加项 + 独立的「⚙」选 MA 周期。
 * 现在合并为一个弹层，左栏选叠加项、右栏配二级设置（MA 周期 / 估值口径），
 * 窄屏退化为单栏纵向排列。触发器只显示已选数量，不占横向空间。
 */
export function OverlayConfig({
  active,
  onChange,
  maPeriods,
  onMaPeriodsChange,
  /** 估值曲线口径（静态 PE / TTM）—— 需求 F2.5 */
  valuationBasis = 'pe2',
  onValuationBasisChange,
  valuationKind = 'pe',
  onValuationKindChange,
  /** 该指数是否在中证估值体系内；不在则估值叠加不可用 */
  valuationSupported = true,
}: {
  active: Set<OverlayId>;
  onChange: (next: Set<OverlayId>) => void;
  maPeriods: MaPeriod[];
  onMaPeriodsChange: (next: MaPeriod[]) => void;
  valuationBasis?: ValuationBasis;
  onValuationBasisChange?: (b: ValuationBasis) => void;
  valuationKind?: ValuationKind;
  onValuationKindChange?: (k: ValuationKind) => void;
  valuationSupported?: boolean;
}) {
  const toggle = (id: OverlayId) => {
    const next = new Set(active);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const toggleMa = (n: MaPeriod, on: boolean) => {
    // 保持 MA_PERIODS 的固定顺序，避免图例/设置项顺序随勾选先后跳动
    const next = on
      ? MA_PERIODS.filter((p) => p === n || maPeriods.includes(p))
      : maPeriods.filter((p) => p !== n);
    onMaPeriodsChange([...next]);
  };

  const [subTab, setSubTab] = useState<SubTab>('ma');

  /**
   * 当前可用的右栏子项：只有「已勾选且有二级设置」的叠加项才进来。
   * MA 与估值同时勾选时给一个小切换器，用户想调哪个点哪个，
   * 不会出现「勾了但看不到设置」的情况。
   */
  const subOptions: SubTab[] = [
    ...(active.has('ma') ? ['ma' as const] : []),
    ...(active.has('valuation') && valuationSupported ? ['valuation' as const] : []),
  ];
  // 勾选项被取消时回落到仍在的那个，避免右栏显示已取消项的设置
  const shown: SubTab | null = subOptions.includes(subTab) ? subTab : subOptions[0] ?? null;

  return (
    <div className="flex items-center gap-1">
      <Popover
        label="指标"
        badge={active.size}
        width={shown ? POPOVER_WIDE : POPOVER_NARROW}
        title="主图叠加指标（可多选）"
      >
        {() => (
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-0">
            {/* 左栏：叠加项勾选 */}
            <div className="flex flex-col sm:border-r sm:pr-2" style={{ borderColor: 'var(--border)' }}>
              {OVERLAYS.map((o) => {
                // 深市系与港股指数不在中证体系内 → 估值叠加无数据可画，显式禁用
                const disabled = o.id === 'valuation' && !valuationSupported;
                return (
                  <PopoverCheck
                    key={o.id}
                    checked={active.has(o.id)}
                    disabled={disabled}
                    onChange={() => toggle(o.id)}
                    label={o.label}
                    title={disabled ? '该指数不在中证估值体系内，无数据可叠加' : o.hint}
                  />
                );
              })}
            </div>

            {/* 右栏：二级设置（MA 周期 / 估值口径） */}
            {shown && (
              <div className="min-w-0 sm:pl-2">
                {subOptions.length > 1 && (
                  <div className="mb-1 flex gap-1">
                    {subOptions.map((k) => (
                      <MiniToggle key={k} on={shown === k} onClick={() => setSubTab(k)}>
                        {SUB_LABEL[k]}
                      </MiniToggle>
                    ))}
                  </div>
                )}
                {shown === 'ma' && (
                  <>
                    <PopoverGroup>常用组合</PopoverGroup>
                    <div className="flex flex-wrap gap-1 pb-1.5">
                      {MA_PRESETS.map((p) => (
                        <MiniToggle
                          key={p.id}
                          on={p.periods.length === maPeriods.length && p.periods.every((x) => maPeriods.includes(x))}
                          onClick={() => onMaPeriodsChange([...p.periods])}
                          title={p.periods.length ? `MA ${p.periods.join(' / ')}` : '不显示任何均线'}
                        >
                          {p.label}
                        </MiniToggle>
                      ))}
                    </div>
                    <div className="border-t pt-1" style={{ borderColor: 'var(--border)' }}>
                      <PopoverGroup>逐条勾选 · 已选 {maPeriods.length} 条</PopoverGroup>
                      {MA_PERIODS.map((n) => (
                        <PopoverCheck
                          key={n}
                          checked={maPeriods.includes(n)}
                          onChange={(on) => toggleMa(n, on)}
                          label={`MA${n}`}
                        />
                      ))}
                    </div>
                  </>
                )}
                {shown === 'valuation' && (
                  <>
                    <PopoverGroup>画什么曲线</PopoverGroup>
                    <div className="flex gap-1 pb-1">
                      {([['pe', 'PE'], ['dividend', '股息'], ['both', 'PE+股息']] as const).map(([id, label]) => (
                        <MiniToggle key={id} on={valuationKind === id} onClick={() => onValuationKindChange?.(id)}>
                          {label}
                        </MiniToggle>
                      ))}
                    </div>
                    {/* 股息率无口径之分 */}
                    {valuationKind !== 'dividend' && (
                      <>
                        <PopoverGroup>PE 口径</PopoverGroup>
                        <div className="flex gap-1">
                          {([['pe1', '静态'], ['pe2', 'TTM']] as const).map(([id, label]) => (
                            <MiniToggle key={id} on={valuationBasis === id} onClick={() => onValuationBasisChange?.(id)}>
                              {label}
                            </MiniToggle>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Popover>
    </div>
  );
}

/**
 * 副图配置 —— 收进单一下拉。
 *
 * 改前是 3 个槽位「点击循环切换指标 + 右键移除」。**右键在移动端不存在**，
 * 且循环切换要点 5 次才能选到目标，属实际的可发现性缺陷。
 * 现改为 3 组复选：勾上即启用，取消即移除。
 */
export function PaneSelector({
  value,
  onChange,
  /**
   * 本周期可用的副图集合（缺省 = 全部）。
   * 分时/5日分时只有分钟级成交量一个可用指标 —— 数据里没有日级收盘序列，
   * MACD/RSI/KDJ 在该形态下没有一致口径，故不提供，避免空槽。
   */
  available,
}: {
  /** 固定长度 3 的槽位数组，null 表示空槽 */
  value: (PaneId | null)[];
  onChange: (next: (PaneId | null)[]) => void;
  available?: PaneId[];
}) {
  const options = available
    ? PANE_OPTIONS.filter((o) => available.includes(o.id))
    : PANE_OPTIONS;
  /**
   * 槽位数组固定长度 3，空槽用 null 占位。
   * 不做「紧凑打包」—— 否则移除中间槽位后其余槽位会左移，
   * 与界面上看到的槽位序号对不上。
   */
  const setSlot = (i: number, id: PaneId | null) => {
    const next = value.map((s, idx) => (idx === i ? id : s));
    const filled = next.filter((x): x is PaneId => x !== null);
    if (filled.length > PANE_MAX_COUNT) return;
    onChange(next);
  };

  const used = value.filter((x): x is PaneId => x !== null);

  return (
    <Popover
      label="副图"
      badge={used.length}
      width={228}
      title={`副图指标（最多 ${PANE_MAX_COUNT} 个，可多选）`}
    >
      {() => (
        <>
          {value.map((slot, i) => {
            const filled = used.length >= PANE_MAX_COUNT && slot === null;
            return (
              <div key={i} className="mb-0.5">
                <PopoverGroup>副图 {i + 1}</PopoverGroup>
                {options.map((o) => (
                  <PopoverCheck
                    key={o.id}
                    // 已占用的槽位不可与其它槽位重复选同一指标
                    disabled={filled || (slot !== null && slot !== o.id)}
                    checked={slot === o.id}
                    onChange={(on) => {
                      if (on) setSlot(i, o.id);
                      else if (slot === o.id) setSlot(i, null);
                    }}
                    label={o.label}
                    title={
                      slot !== null && slot !== o.id
                        ? '该指标已在其它副图中'
                        : filled
                          ? `副图已达上限（${PANE_MAX_COUNT} 个）`
                          : undefined
                    }
                  />
                ))}
              </div>
            );
          })}
        </>
      )}
    </Popover>
  );
}

/** 弹层内的小胶囊单选按钮 */
function MiniToggle({
  on, onClick, title, children,
}: { on: boolean; onClick: () => void; title?: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      title={title}
      className="rounded px-1.5 py-0.5 text-[11px] transition-colors"
      style={{
        background: on ? 'var(--bg-elevated)' : 'transparent',
        border: `1px solid ${on ? 'var(--border-strong)' : 'var(--border)'}`,
        color: on ? 'var(--text-1)' : 'var(--text-3)',
      }}
    >
      {children}
    </button>
  );
}